import canonicalize from 'canonicalize';

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });
export type Binary = ArrayBufferLike | ArrayBufferView<ArrayBufferLike>;

export function utf8(value: string): Uint8Array<ArrayBuffer> { return encoder.encode(value); }
export function fromUtf8(value: Binary): string { return decoder.decode(asBytes(value)); }

export function asBytes(value: Binary): Uint8Array<ArrayBuffer> {
  const source = ArrayBuffer.isView(value)
    ? new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
    : new Uint8Array(value);
  return Uint8Array.from(source);
}

export function base64url(value: Binary): string {
  const bytes = asBytes(value);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

export function unbase64url(value: string, length?: number): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]*$/u.test(value)) throw new Error('Ungültiges Base64url-Format.');
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/') + padding);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (length !== undefined && bytes.length !== length) throw new Error(`Binärwert muss ${length} Byte lang sein.`);
  return bytes;
}

export function concat(...values: Binary[]): Uint8Array<ArrayBuffer> {
  const parts = values.map(asBytes);
  const result = new Uint8Array(parts.reduce((length, part) => length + part.length, 0));
  let offset = 0;
  for (const part of parts) { result.set(part, offset); offset += part.length; }
  return result;
}

export function canonicalBytes(value: unknown): Uint8Array<ArrayBuffer> {
  const encoded = canonicalize(value);
  if (encoded === undefined) throw new Error('Der Wert kann nicht nach RFC 8785 kanonisiert werden.');
  return utf8(encoded);
}

export function withoutSignature<T extends Record<string, unknown>>(value: T): Omit<T, 'signature'> {
  const signed: Record<string, unknown> = { ...value };
  delete signed.signature;
  return signed as Omit<T, 'signature'>;
}

export function equalBytes(left: Binary, right: Binary): boolean {
  const a = asBytes(left); const b = asBytes(right);
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let index = 0; index < a.length; index++) difference |= a[index]! ^ b[index]!;
  return difference === 0;
}
